import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomButton } from '../../../components/buttons/customButton';
import { useOrganizationSettingsStore } from '../../../store/organizationSettingsStore';
import { useUpdateOnboardingStep } from '../../../apiHooks/mutations/onboarding/useUpdateOnboardingStep';
import { useVerifyTracking, VerifyTrackingResponse } from '../../../apiHooks/mutations/onboarding/useVerifyTracking';

interface TrackingStepProps {
  onContinue: () => void;
}

const SHOPIFY_DOCS_URL = 'https://knowledge.digtective.com/installing-digger-on-your-shopify-store';
const WORDPRESS_DOCS_URL = 'https://knowledge.digtective.com/how-to-integrate-digtective-wordpress-plugin-into-my-business';

const COOLDOWN_SECONDS = 120;

const TrackingStep: FC<TrackingStepProps> = ({ onContinue }) => {
  const { t } = useTranslation();
  const organizationSettings = useOrganizationSettingsStore((s) => s.organizationSettings);
  const { mutateAsync: updateStep, isLoading: isSaving } = useUpdateOnboardingStep();
  const { mutateAsync: verifyTracking, isLoading: isVerifying } = useVerifyTracking();

  const integrationType = organizationSettings.integrationType;
  const website = organizationSettings.website;
  const isCustom = integrationType === 'custom';

  const [verified, setVerified] = useState(false);
  const [verifyError, setVerifyError] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = useCallback((seconds: number) => {
    setCooldownRemaining(seconds);
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = setInterval(() => {
      setCooldownRemaining((prev) => {
        if (prev <= 1) {
          if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => {
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
  }, []);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleVerify = async () => {
    setVerifyError(false);
    try {
      const result: VerifyTrackingResponse = await verifyTracking();
      if (result.found) {
        setVerified(true);
      } else {
        setVerifyError(true);
        startCooldown(COOLDOWN_SECONDS);
      }
    } catch (err: any) {
      const responseData = err?.response?.data?.data;
      if (err?.response?.status === 429 && responseData?.retryAfterSeconds) {
        startCooldown(responseData.retryAfterSeconds);
      } else {
        setVerifyError(true);
        startCooldown(COOLDOWN_SECONDS);
      }
    }
  };

  const handleContinue = async () => {
    await updateStep({ step: 'tracking', status: 'completed' });
    onContinue();
  };

  if (isCustom) {
    return (
      <div className="flex w-full max-w-3xl flex-col gap-6">
        <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
          <h1 className="mb-1.5 text-center text-lg font-semibold text-gray-900">
            {t('setup.tracking.custom.title')}
          </h1>
          <p className="mb-2 text-center text-sm text-gray-500">
            {t('setup.tracking.custom.subtitle')}
          </p>
          <p className="mb-8 text-center text-sm leading-relaxed text-gray-500">
            {t('setup.tracking.custom.message')}
          </p>
          <a
            href={`mailto:${t('setup.tracking.custom.email')}`}
            className="mx-auto flex w-fit items-center gap-2 rounded-xl border-2 border-primary/20 bg-primary/5 px-6 py-3 text-sm font-semibold text-primary transition-all hover:border-primary/40 hover:shadow-md"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="M22 4L12 13 2 4" />
            </svg>
            {t('setup.tracking.custom.emailLabel')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-3xl flex-col gap-6">
      {/* Header */}
      <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
        <h1 className="mb-2 text-center text-lg font-semibold text-gray-900">
          {t('setup.tracking.title')}
        </h1>
        <p className="text-center text-sm leading-relaxed text-gray-500">
          {t('setup.tracking.subtitle')}
        </p>
      </div>

      {/* Step 1 — Installation Guides */}
      <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
        <h2 className="mb-1.5 text-base font-semibold text-gray-900">
          {t('setup.tracking.step1.title')}
        </h2>
        <p className="mb-5 text-sm leading-relaxed text-gray-500">
          {t(`setup.tracking.step1.description.${integrationType ?? 'wordpress'}`)}
        </p>

        <div className="flex flex-col gap-3">
          <a
            href={SHOPIFY_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-sm font-semibold transition-all hover:shadow-md ${
              integrationType === 'shopify'
                ? 'border-primary/30 bg-primary/5 text-primary hover:border-primary/50'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            }`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            {t('setup.tracking.docsLinkLabel.shopify')}
          </a>
          <a
            href={WORDPRESS_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 rounded-xl border-2 px-4 py-3.5 text-sm font-semibold transition-all hover:shadow-md ${
              integrationType === 'wordpress' || integrationType === 'woocommerce'
                ? 'border-primary/30 bg-primary/5 text-primary hover:border-primary/50'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            }`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            {t('setup.tracking.docsLinkLabel.wordpress')}
          </a>
        </div>
      </div>

      {/* Step 2 — Verify */}
      <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-sm">
        <h2 className="mb-1.5 text-base font-semibold text-gray-900">
          {t('setup.tracking.step2.title')}
        </h2>
        <p className="mb-5 text-sm leading-relaxed text-gray-500">
          {t('setup.tracking.step2.description')}
        </p>

        {website && (
          <div className="mb-5">
            <span className="mb-1.5 block text-xs font-medium text-gray-400">
              {t('setup.tracking.checkingWebsite')}
            </span>
            <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-4 py-2.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gray-400">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <span className="text-sm font-medium text-gray-700">{website}</span>
            </div>
          </div>
        )}

        {verified && (
          <div className="mb-5 rounded-xl border-2 border-green bg-green/5 px-4 py-4">
            <div className="flex items-center gap-2.5">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M2 7l3.5 3.5L12 4" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="text-sm font-semibold text-green">{t('setup.tracking.found')}</span>
            </div>
            <p className="mt-1.5 pl-[34px] text-sm leading-relaxed text-gray-500">
              {t('setup.tracking.foundDescription')}
            </p>
          </div>
        )}

        {verifyError && !verified && (
          <div className="mb-5 rounded-xl border-2 border-red-200 bg-red-50 px-4 py-4">
            <div className="flex items-center gap-2.5">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M4 4l6 6M10 4l-6 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <span className="text-sm font-semibold text-red-600">{t('setup.tracking.notFound')}</span>
            </div>
            <p className="mt-1.5 pl-[34px] text-sm leading-relaxed text-gray-500">
              {t('setup.tracking.notFoundDescription')}
            </p>
          </div>
        )}

        {!verified && (
          <CustomButton
            className="w-full justify-center py-2.5"
            disabled={isVerifying || cooldownRemaining > 0}
            onClick={handleVerify}
          >
            {isVerifying
              ? t('setup.tracking.verifying')
              : cooldownRemaining > 0
                ? t('setup.tracking.cooldown', { time: formatTime(cooldownRemaining) })
                : t('setup.tracking.verifyButton')}
          </CustomButton>
        )}
      </div>

      {/* Continue */}
      {verified && (
        <CustomButton
          className="w-full justify-center py-2.5"
          disabled={isSaving}
          onClick={handleContinue}
        >
          {isSaving ? t('setup.saving') : `${t('setup.continue')} \u2192`}
        </CustomButton>
      )}
    </div>
  );
};

export default TrackingStep;
